import React from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const faqs = [
  {
    slug: 'what-is-texsolution',
    question: 'What is TexSolution?',
    answer: 'TexSolution is a next-generation digital wallet designed for global payments. It combines QR code payments, SWIFT/SEPA transfers, and cross-border payouts in a single platform — making it easy to manage finances across borders without needing multiple foreign bank accounts or cards.',
  },
  {
    slug: 'need-foreign-card',
    question: 'Do I need a foreign bank card?',
    answer: 'No. TexSolution eliminates the need for foreign bank cards entirely. You can fund your wallet in your local currency and make payments globally. The platform handles all currency conversion automatically with transparent rates.',
  },
  {
    slug: 'qr-countries',
    question: 'In which countries does QR payment work?',
    answer: 'QR code payments are currently available in 18 countries including Turkey, Thailand, UAE, Japan, Singapore, Vietnam, China, Brazil, India, Indonesia, Malaysia, Philippines, Sri Lanka, Egypt, South Korea, UK, Germany, and the USA. We are adding new markets quarterly.',
  },
  {
    slug: 'how-fund',
    question: 'How do I fund my wallet?',
    answer: 'You can top up your TexSolution wallet via bank transfer, credit/debit card, or local payment methods depending on your region. Multiple currencies are supported, and funds are available almost immediately after processing.',
  },
  {
    slug: 'security',
    question: 'How secure is TexSolution?',
    answer: 'Security is at the core of everything we build. TexSolution uses end-to-end encryption, multi-factor authentication, real-time fraud monitoring, and complies with international financial regulations. Your funds and data are protected by enterprise-grade infrastructure.',
  },
  {
    slug: 'business',
    question: 'Can businesses use TexSolution?',
    answer: 'Yes. We offer business accounts with features like batch payouts, API integration, detailed reporting, and dedicated support. Our partner-ready architecture enables seamless integration with existing business systems.',
  },
  {
    slug: 'fees',
    question: 'What are the fees?',
    answer: 'TexSolution offers competitive, transparent pricing. There are no hidden fees — you see the exact cost before confirming any transaction. Fees vary by transaction type and destination, and we always show the best available rates.',
  },
];

export const FAQ = () => {
  return (
    <section id="faq" className="py-16 sm:py-20 bg-white" data-testid="faq-section">
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left: Header + support card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2"
          >
            <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.18em] text-[#7C3AED] mb-3">
              FAQ
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0B0B10] mb-4">
              Frequently asked questions
            </h2>
            <p className="text-base text-[#64647A] mb-6 leading-relaxed">
              Can't find the answer you're looking for? Reach out to our support team.
            </p>

            {/* Support card */}
            <div className="bg-[#F5F3FF] rounded-2xl p-6">
              <h3 className="text-base font-semibold text-[#0B0B10] mb-2">Need more help?</h3>
              <p className="text-sm text-[#64647A] mb-4">Our support team is available 24/7 to assist you with any questions.</p>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm font-medium text-[#7C3AED] hover:text-[#6D28D9] transition-colors"
              >
                Contact support →
              </button>
            </div>
          </motion.div>

          {/* Right: Accordion */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-3"
          >
            <Accordion type="single" collapsible data-testid="faq-accordion">
              {faqs.map((faq) => (
                <AccordionItem
                  key={faq.slug}
                  value={faq.slug}
                  data-testid={`faq-item-${faq.slug}`}
                  className="border-b border-[#E7E7EF]"
                >
                  <AccordionTrigger
                    data-testid={`faq-item-trigger-${faq.slug}`}
                    className="text-left text-base font-medium text-[#0B0B10] hover:text-[#7C3AED] py-5"
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-item-content-${faq.slug}`}
                    className="text-sm text-[#64647A] leading-relaxed pb-5"
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
